import { useState, useRef, useCallback, useEffect } from "react";
import { requestSystemAudio, requestMicAudio, stopMediaStream } from "../services/mediaService";
import { fetchDeepgramToken, connectToDeepgram, type DeepgramConnection } from "../services/deepgramService";
import { createMeeting } from "../services/meetingService";
import { useUiLang } from "../../../context/UiLangContext";
import { toast } from "react-hot-toast";

export type RecordingStatus = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "error";

interface UseRecordingProps {
  translatorServiceUrl: string;
  srcLang: string;
  tgtLang: string;
  voiceEnabled: boolean;
  appMode: "traductor" | "minuta";
  onAddHistory: (orig: string, tran: string, isMic?: boolean) => void;
  onUpdateSubtitle: (orig: string, tran: string) => void;
  onTranslateText: (text: string, src: string, tgt: string) => Promise<string>;
  onSpeakTranslation: (text: string, langCode: string, force?: boolean, voiceEnabled?: boolean) => void;
  onUpdatePip: (orig: string, tran: string) => void;
  setCurrentMeetingId: (id: string | null) => void;
  setMeetingTranscript: (t: any[]) => void;
}

export function useRecording({
  translatorServiceUrl,
  srcLang,
  tgtLang,
  voiceEnabled,
  appMode,
  onAddHistory,
  onUpdateSubtitle,
  onTranslateText,
  onSpeakTranslation,
  onUpdatePip,
  setCurrentMeetingId,
  setMeetingTranscript,
}: UseRecordingProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [statusText, setStatusText] = useState(isEn ? "Ready to start" : "Listo para iniciar");

  const sysConnRef = useRef<DeepgramConnection | null>(null);
  const micConnRef = useRef<DeepgramConnection | null>(null);
  const streamRef = useRef<MediaStream[]>([]);
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for current values in event listeners
  const srcLangRef = useRef(srcLang);
  const tgtLangRef = useRef(tgtLang);
  const voiceEnabledRef = useRef(voiceEnabled);
  const appModeRef = useRef(appMode);
  const isEnRef = useRef(isEn);
  const retryCountRef = useRef(0);
  const recordingRef = useRef(recording);

  srcLangRef.current = srcLang;
  tgtLangRef.current = tgtLang;
  voiceEnabledRef.current = voiceEnabled;
  appModeRef.current = appMode;
  isEnRef.current = isEn;
  recordingRef.current = recording;

  useEffect(() => {
    if (status === "idle") {
      setStatusText(uiLang === "en" ? "Ready to start" : "Listo para iniciar");
    }
  }, [uiLang, status]);

  const stopRecording = useCallback(() => {
    retryCountRef.current = 0;
    setRecording(false);
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    setStatus("disconnected");
    setStatusText(isEnRef.current ? "Disconnected" : "Desconectado");

    const sys = sysConnRef.current;
    if (sys) {
      sysConnRef.current = null;
      sys.close();
    }
    const mic = micConnRef.current;
    if (mic) {
      micConnRef.current = null;
      mic.close();
    }

    streamRef.current.forEach(stopMediaStream);
    streamRef.current = [];

    statusTimeoutRef.current = setTimeout(() => {
      setStatus("idle");
      setStatusText(isEnRef.current ? "Ready to start" : "Listo para iniciar");
    }, 2000);
  }, []);

  const handleUnexpectedClose = useCallback(() => {
    if (!sysConnRef.current || !recordingRef.current) return;
    sysConnRef.current = null;

    if (retryCountRef.current < 3) {
      retryCountRef.current += 1;
      setStatus("reconnecting");
      setStatusText(isEnRef.current ? `Reconnecting (${retryCountRef.current}/3)...` : `Reconectando (${retryCountRef.current}/3)...`);
      
      setTimeout(async () => {
        if (!recordingRef.current) return;
        try {
          const newToken = await fetchDeepgramToken(translatorServiceUrl);
          const displayStream = streamRef.current[0];
          if (!displayStream || !recordingRef.current) return;

          const currentSrcLang = srcLangRef.current;
          const currentTgtLang = tgtLangRef.current;
          const currentAppMode = appModeRef.current;
          const currentIsEn = isEnRef.current;
          const sysUrl = currentSrcLang === "auto"
            ? "wss://api.deepgram.com/v1/listen?model=nova-3&smart_format=true&language=multi"
            : `wss://api.deepgram.com/v1/listen?model=nova-3&language=${currentSrcLang}&smart_format=true`;

          sysConnRef.current = connectToDeepgram({
            url: sysUrl,
            token: newToken,
            stream: displayStream,
            onOpen: () => {
              retryCountRef.current = 0;
              setStatus("connected");
              setStatusText(currentIsEn ? "CONNECTED" : "CONECTADO");
              toast.success(currentIsEn ? "Reconnected to Deepgram" : "Reconectado con Deepgram");
            },
            onTranscript: async (transcript, detectedLang) => {
              const langToTranslateFrom = currentSrcLang === "auto" && detectedLang ? detectedLang : currentSrcLang;
              const translated = await onTranslateText(transcript, langToTranslateFrom, currentTgtLang);
              onUpdateSubtitle(transcript, translated);
              onUpdatePip(transcript, translated);
              onAddHistory(transcript, translated, false);
              if (currentAppMode === "traductor") {
                onSpeakTranslation(translated, currentTgtLang, false, voiceEnabledRef.current);
              }
            },
            onError: () => {
              handleUnexpectedClose();
            },
            onClose: () => {
              handleUnexpectedClose();
            }
          });
        } catch (err) {
          console.error("Reconnection failed:", err);
          handleUnexpectedClose();
        }
      }, 2000);
    } else {
      setStatus("error");
      setStatusText(isEnRef.current ? "Connection lost after 3 retries" : "Conexión perdida tras 3 intentos");
      setRecording(false);
      toast.error(isEnRef.current ? "Deepgram connection closed unexpectedly." : "La conexión con Deepgram se cerró inesperadamente.");
      
      const mic = micConnRef.current;
      if (mic) {
        micConnRef.current = null;
        mic.close();
      }
      streamRef.current.forEach(stopMediaStream);
      streamRef.current = [];
    }
  }, [
    translatorServiceUrl, onTranslateText, onUpdateSubtitle, onUpdatePip, onAddHistory, onSpeakTranslation
  ]);

  const startRecording = useCallback(async (onStartedCb?: () => void) => {
    try {
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      setStatus("connecting");
      setStatusText(isEnRef.current ? "Starting connectivity..." : "Iniciando conectividad...");

      const currentSrcLang = srcLangRef.current;
      const currentTgtLang = tgtLangRef.current;
      const currentAppMode = appModeRef.current;
      const currentIsEn = isEnRef.current;

      const sysUrl =
        currentSrcLang === "auto"
          ? "wss://api.deepgram.com/v1/listen?model=nova-3&smart_format=true&language=multi"
          : `wss://api.deepgram.com/v1/listen?model=nova-3&language=${currentSrcLang}&smart_format=true`;

      const micUrl = "wss://api.deepgram.com/v1/listen?model=nova-3&language=es&smart_format=true";

      // 1. Create meeting in backend
      const meetingId = await createMeeting(translatorServiceUrl, currentSrcLang, currentTgtLang, currentAppMode);
      if (meetingId) {
        setCurrentMeetingId(meetingId);
        setMeetingTranscript([]); // clean old history
      }

      // 2. Fetch Deepgram Token
      let deepgramAccessToken = "";
      try {
        deepgramAccessToken = await fetchDeepgramToken(translatorServiceUrl);
      } catch (err) {
        setStatus("error");
        setStatusText(currentIsEn ? "AI Token Error" : "Error en Token de IA");
        return;
      }

      // 3. Request Media
      setStatusText(currentIsEn ? "Requesting audio permissions..." : "Solicitando permisos de audio...");
      const displayStream = await requestSystemAudio();
      const micStream = await requestMicAudio();

      streamRef.current.push(displayStream);
      if (micStream) streamRef.current.push(micStream);

      const sysTrack = displayStream.getAudioTracks()[0];
      if (sysTrack) {
        sysTrack.onended = () => stopRecording();
      }

      // 4. Initialize System WebSocket
      sysConnRef.current = connectToDeepgram({
        url: sysUrl,
        token: deepgramAccessToken,
        stream: displayStream,
        onOpen: () => {
          retryCountRef.current = 0;
          setStatus("connected");
          setStatusText(currentIsEn ? "CONNECTED" : "CONECTADO");
          setRecording(true);
          const modeStr = currentAppMode === "traductor"
            ? (currentIsEn ? "Translator + Minutes" : "Traductor + Minuta")
            : (currentIsEn ? "Minutes Only" : "Solo Minuta");
          toast.success(currentIsEn ? `Recording started · ${modeStr}` : `Grabación iniciada · ${modeStr}`);
          if (onStartedCb) onStartedCb();
        },
        onTranscript: async (transcript, detectedLang) => {
          const langToTranslateFrom = currentSrcLang === "auto" && detectedLang ? detectedLang : currentSrcLang;
          const translated = await onTranslateText(transcript, langToTranslateFrom, currentTgtLang);

          onUpdateSubtitle(transcript, translated);
          onUpdatePip(transcript, translated);
          onAddHistory(transcript, translated, false);

          if (currentAppMode === "traductor") {
            onSpeakTranslation(translated, currentTgtLang, false, voiceEnabledRef.current);
          }
        },
        onError: () => {
          handleUnexpectedClose();
        },
        onClose: () => {
          handleUnexpectedClose();
        }
      });

      // 5. Initialize Mic WebSocket (if mic exists)
      if (micStream) {
        micConnRef.current = connectToDeepgram({
          url: micUrl,
          token: deepgramAccessToken,
          stream: micStream,
          onTranscript: async (transcript) => {
            const translated = await onTranslateText(transcript, "es", currentTgtLang);

            onUpdateSubtitle(transcript, translated);
            onUpdatePip(`🎤 Yo: ${transcript}`, `🎤 Yo: ${translated}`);
            onAddHistory(transcript, translated, true);

            if (currentAppMode === "traductor") {
              onSpeakTranslation(translated, currentTgtLang, false, voiceEnabledRef.current);
            }
          },
          onError: () => {
            console.error("Mic WebSocket error");
          },
          onClose: () => {
            if (micConnRef.current) {
              micConnRef.current = null;
              toast.error(currentIsEn ? "Microphone connection lost." : "Conexión de micrófono interrumpida.");
            }
          }
        });
      }

    } catch (err: any) {
      console.error(err);
      setStatus("error");
      const currentIsEn = isEnRef.current;
      let errorMsg = err?.message || (currentIsEn ? "Failed to start" : "Error al iniciar");
      if (
        errorMsg.includes("Permission denied") ||
        errorMsg.includes("NotAllowedError") ||
        err?.name === "NotAllowedError" ||
        errorMsg.includes("Permiso denegado")
      ) {
        errorMsg = currentIsEn
          ? "Permission denied by user"
          : "Permiso denegado por el usuario";
      } else if (errorMsg.includes("No seleccionaste") || errorMsg.includes("audio del sistema")) {
        errorMsg = currentIsEn
          ? "System audio not shared"
          : "No seleccionaste 'Compartir audio del sistema'";
      }
      setStatusText(errorMsg);
      streamRef.current.forEach(stopMediaStream);
      streamRef.current = [];

      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => {
        setStatus("idle");
        setStatusText(isEnRef.current ? "Ready to start" : "Listo para iniciar");
      }, 3000);
    }
  }, [
    stopRecording, onTranslateText, onUpdateSubtitle, onUpdatePip, onAddHistory, onSpeakTranslation,
    translatorServiceUrl, setCurrentMeetingId, setMeetingTranscript
  ]);

  return {
    recording,
    status,
    statusText,
    startRecording,
    stopRecording,
  };
}
